import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Plus, Trash2, Pencil, Info, AlertTriangle, OctagonAlert } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import MediaUploader from "@/components/MediaUploader";
import MediaGrid from "@/components/MediaGrid";

export const NOTE_LEVELS = {
  info: { label: "Info", icon: Info, cls: "bg-sky-50 text-sky-700 border-sky-200" },
  achtung: { label: "Achtung", icon: AlertTriangle, cls: "bg-amber-50 text-amber-700 border-amber-200" },
  warnung: { label: "Warnung", icon: OctagonAlert, cls: "bg-red-50 text-red-700 border-red-200" },
};

function NotePhotos({ noteId }) {
  const [photos, setPhotos] = useState([]);
  const load = useCallback(() => api.get(`/notes/${noteId}/photos`).then((r) => setPhotos(r.data)).catch(() => {}), [noteId]);
  useEffect(() => { load(); }, [load]);
  return (
    <div className="mt-2 space-y-2" data-testid={`note-photos-${noteId}`}>
      <MediaUploader links={{ note_id: noteId }} onUploaded={load} compact />
      {photos.length > 0 && <MediaGrid photos={photos} onChanged={load} cols="grid-cols-4" />}
    </div>
  );
}

function NoteForm({ positionId, note, onSaved, onCancel }) {
  const [f, setF] = useState({ level: note?.level || "info", title: note?.title || "", description: note?.description || "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.title.trim()) return toast.error("Bitte einen Titel eingeben");
    setBusy(true);
    try {
      if (note) await api.put(`/notes/${note.id}`, f); else await api.post(`/positions/${positionId}/notes`, f);
      toast.success("Hinweis gespeichert");
      onSaved();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    setBusy(false);
  };
  return (
    <div className="rounded-xl border border-slate-200 p-3 space-y-2 bg-slate-50" data-testid="note-form">
      <div className="flex gap-1.5">
        {Object.entries(NOTE_LEVELS).map(([k, L]) => (
          <button key={k} type="button" onClick={() => setF({ ...f, level: k })} data-testid={`note-level-${k}`}
            className={`flex-1 inline-flex items-center justify-center gap-1 rounded-lg border px-2 py-1.5 text-xs font-semibold transition-colors ${f.level === k ? L.cls : "bg-white text-slate-500 border-slate-200"}`}>
            <L.icon className="h-3.5 w-3.5" />{L.label}
          </button>
        ))}
      </div>
      <Input placeholder="Titel" value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} data-testid="note-title-input" />
      <Textarea placeholder="Beschreibung" rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="note-description-input" />
      <div className="flex justify-end gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel} data-testid="note-cancel-btn">Abbrechen</Button>
        <Button size="sm" onClick={save} disabled={busy} data-testid="note-save-btn">Speichern</Button>
      </div>
    </div>
  );
}

function NoteCard({ n, onEdit, onDeleted }) {
  const L = NOTE_LEVELS[n.level] || NOTE_LEVELS.info;
  const del = async () => {
    if (!window.confirm("Hinweis mit Fotos löschen?")) return;
    await api.delete(`/notes/${n.id}`).then(() => { toast.success("Hinweis gelöscht"); onDeleted(); }).catch(() => toast.error("Löschen nicht möglich"));
  };
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-3" data-testid={`note-card-${n.id}`}>
      <div className="flex items-start gap-2">
        <span className={`inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-[11px] font-semibold shrink-0 ${L.cls}`}><L.icon className="h-3 w-3" />{L.label}</span>
        <div className="flex-1 min-w-0">
          <div className="font-semibold text-sm text-slate-900">{n.title}</div>
          {n.description && <p className="text-xs text-slate-600 whitespace-pre-line mt-0.5">{n.description}</p>}
        </div>
        <button onClick={onEdit} className="p-1 text-slate-400 hover:text-primary" data-testid={`note-edit-${n.id}`} title="Bearbeiten"><Pencil className="h-3.5 w-3.5" /></button>
        <button onClick={del} className="p-1 text-slate-400 hover:text-red-600" data-testid={`note-delete-${n.id}`} title="Löschen"><Trash2 className="h-3.5 w-3.5" /></button>
      </div>
      <NotePhotos noteId={n.id} />
    </div>
  );
}

// Tab "Hinweise" in the position window; notes are always included in every generated report.
export default function PositionNotesTab({ positionId, onChange }) {
  const [notes, setNotes] = useState([]);
  const [editing, setEditing] = useState(null); // null | "new" | note
  const load = useCallback(() => api.get(`/positions/${positionId}/notes`).then((r) => setNotes(r.data)).catch(() => setNotes([])), [positionId]);
  useEffect(() => { load(); }, [load]);
  const done = () => { setEditing(null); load(); onChange && onChange(); };
  return (
    <div className="space-y-3" data-testid="notes-tab">
      <p className="text-xs text-slate-500">Hinweise erscheinen in allen erstellten Berichten bei dieser Position.</p>
      {editing === "new" ? <NoteForm positionId={positionId} onSaved={done} onCancel={() => setEditing(null)} />
        : <Button size="sm" variant="outline" onClick={() => setEditing("new")} data-testid="add-note-btn"><Plus className="h-4 w-4 mr-1" />Neuer Hinweis</Button>}
      {notes.length === 0 && editing !== "new" && <p className="text-sm text-slate-400" data-testid="notes-empty">Noch keine Hinweise.</p>}
      {notes.map((n) => (editing?.id === n.id
        ? <NoteForm key={n.id} positionId={positionId} note={n} onSaved={done} onCancel={() => setEditing(null)} />
        : <NoteCard key={n.id} n={n} onEdit={() => setEditing(n)} onDeleted={done} />))}
    </div>
  );
}
